import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import localforage from 'localforage'
import { queryClient, trpcClient } from './trpc'

/**
 * Helper to check authentication status and redirect to login if needed
 */
export function useAuthCheck() {
  const navigate = useNavigate()

  // Function to check auth status and redirect if needed
  // const checkAuth = async () => {
  //   // First try the electron API if available
  //   const { token, expiry } = await getAuthToken()

  //   if (!token || !expiry) {
  //     clearAuthData()
  //     toast.error('You need to login to access this page')
  //     navigate({ to: '/login?error=session-not-found&redirect_uri=' + window.location.pathname })
  //     return false
  //   }

  //   if (Date.now() >= expiry) {
  //     toast.error('Your session has expired. Please login again.')
  //     navigate({ to: '/login?error=session-expired&redirect_uri=' + window.location.pathname })
  //     return false
  //   }

  //   return true
  // }

  // Set up an event listener for auth expired events from the main process
  const setupAuthListener = () => {
    // Only setup if we're in Electron
    if (!window._main_api?.onAuthSessionExpired) return () => { }

    return window._main_api.onAuthSessionExpired(() => {
      toast.error('Your session has expired. Please login again.')
      navigate({ to: '/login?error=session-expired&redirect_uri=' + encodeURIComponent(window.location.href) })
    })
  }

  return { setupAuthListener }
}

/**
 * Save authentication data consistently between web and electron environments
 */
export function saveAuthData(token: string, expiryMs: number) {
  finishLogout()

  // Store in localStorage for web app use
  localStorage.setItem('auth:token', token)
  localStorage.setItem('auth:expiry', expiryMs.toString())

  // If in Electron, also send to main process
  if (window._main_api?.sendAuthTokenToMain) {
    window._main_api.sendAuthTokenToMain(token, expiryMs)
  }

  return { token, expiryMs }
}

let loggingOut = false

/** True while an intentional sign-out is in progress (suppresses 401 → login redirects). */
export function isLoggingOut() {
  return loggingOut
}

/** Reset logout state after a new authenticated session is established. */
export function finishLogout() {
  loggingOut = false
}

/**
 * Call BEFORE sign-out: cancel in-flight queries and mark logout so 401 handlers
 * don't race ahead with `error=unauthenticated` on the login URL.
 */
export async function beginLogout() {
  loggingOut = true
  await cancelAllQueries()
}

/**
 * The full sign-out ritual, in order: cancel queries, revoke other sessions,
 * Better-Auth signOut (while the token is still valid), clear caches, remount
 * the Jotai store, redirect. One place — call sites (app sidebar,
 * onboarding wizard header) must not re-inline the sequence.
 *
 * Signing out means signing out everywhere. Neither the browser extension nor
 * the desktop app rides this cookie — both hold their own session, and would
 * otherwise stay authenticated after an explicit sign-out. `revokeOtherSessions`
 * closes that, and runs BEFORE `signOut` so the call is still authenticated.
 */
export async function performFullLogout(input: {
  signOut: () => Promise<unknown>
  logoutJotaiStore: () => void
  navigate: (options: { to: '/login'; replace?: boolean }) => void
}) {
  try {
    await beginLogout()
    try {
      await trpcClient.session.revokeOtherSessions.mutate()
    } catch (error) {
      // Never block sign-out on this. Failing here leaves other surfaces signed
      // in, which is worth logging, but trapping the user in a half-logged-out
      // web session would be worse.
      console.error('Failed to revoke other sessions during logout:', error)
    }
    await input.signOut()
    await clearAuthData()
    input.logoutJotaiStore()
  } catch (error) {
    console.error('Error signing out:', error)
  }
  redirectToLoginAfterLogout(input.navigate)
}

/** Client-side login redirect after logout; no-op if already on /login (avoids full reload). */
export function redirectToLoginAfterLogout(
  navigate: (options: { to: '/login'; replace?: boolean }) => void,
) {
  if (window.location.pathname.startsWith('/login')) return
  navigate({ to: '/login', replace: true })
}

/**
 * Cancel all pending queries - call this BEFORE sign-out to prevent 401 errors
 */
export async function cancelAllQueries() {
  try {
    await queryClient.cancelQueries()
    console.log('>>> All pending queries cancelled')
  } catch (error) {
    console.error('Error cancelling queries:', error)
  }
}

/**
 * localStorage keys that carry workspace/org context — i.e. the identifiers
 * that become stale if the user's workspace/org IDs are rewritten or the
 * record is deleted server-side. Subset of the full auth cache; kept in one
 * place so `clearAuthData` (full sign-out) and stale-id recovery don't drift.
 */
const ACTIVE_WORKSPACE_CONTEXT_KEYS = [
  'auth:activeOrg',
  'auth:activeWorkspace',
  'auth:activeWorkspaceId',
  'partner:delegation',
] as const

const WORKSPACE_CONTEXT_KEYS = [
  ...ACTIVE_WORKSPACE_CONTEXT_KEYS,
  'auth:session',
] as const

/**
 * Clear only active workspace/org selection. Used after a fresh login so the
 * next protected-route load resolves context from the server session instead
 * of reusing another account's localStorage state.
 */
export function clearActiveWorkspaceContext(): void {
  for (const key of ACTIVE_WORKSPACE_CONTEXT_KEYS) {
    localStorage.removeItem(key)
  }
}

/**
 * Clear cached workspace/organization context without ending the session.
 * Used by stale-id recovery: keeps `auth:token` / `auth:expiry` so the user
 * stays signed in while we re-resolve their active org/workspace.
 */
export function clearWorkspaceContextCache(): void {
  for (const key of WORKSPACE_CONTEXT_KEYS) {
    localStorage.removeItem(key)
  }
}

/**
 * Clear authentication data consistently between web and electron environments
 * This clears ALL cache layers: localStorage, IndexedDB (localforage), and React Query cache
 */
export async function clearAuthData() {
  console.log('>>> Clearing all authentication and cache data')

  // 1. Cancel any pending queries first to prevent 401 errors during logout
  await cancelAllQueries()

  // 2. Clear from localStorage
  clearWorkspaceContextCache()
  localStorage.removeItem('auth:token')
  localStorage.removeItem('auth:expiry')

  // 3. Clear IndexedDB (localforage) - document cache and other persistent data
  try {
    await localforage.clear()
    console.log('>>> LocalForage cache cleared')
  } catch (error) {
    console.error('Error clearing localforage:', error)
  }

  // 4. Clear React Query cache - all API queries
  try {
    queryClient.clear()
    console.log('>>> React Query cache cleared')
  } catch (error) {
    console.error('Error clearing query cache:', error)
  }

  // 5. If in Electron, also clear from main process
  if (window._main_api?.signOut) {
    console.log('>>> Clearing auth data in main process')
    window._main_api.signOut()
  }
}

export async function getAuthToken() {
  let token = null
  let expiry = null
  
  if (window._main_api) {
    try {
      const result = await window._main_api.getAuthToken()
      // console.log('>>> main API getAuthToken', result)
      token = result?.token
      expiry = result?.expiry
    } catch (error) {
      console.error('Error validating auth via Electron:', error)
      // Fall through to local storage check
    }
  }

  if (!token || !expiry) {
    // Fallback to localStorage check
    token = localStorage.getItem('auth:token')
    expiry = localStorage.getItem('auth:expiry')
  }

  if (expiry) {
    try {
      const expiryTime = parseInt(expiry, 10)
      return { token, expiry: expiryTime }
    } catch (error) {
      console.error('Error parsing expiry:', error)
      return { token, expiry: null }
    }
  } else {
    return { token, expiry: null }
  }
}
